import { DataTable, FlexBox, IconButton } from '@dixa/lib-shared-habla';
import { Satisfaction } from 'lib-shared-legacy-types';
import { useCallback, useEffect } from 'react';

import { ratingApi } from 'apis/rating';
import { appActions } from 'app/app.actions';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppNavigate } from 'utils/url/useAppNavigate';

export function SurveysTable(): JSX.Element {
  const [
    readRatingSettings,
    {
      ratingSettings: surveyRatingSettings,
      isFetching: readSmsRatingSettingsIsFetching,
      isSuccess: readSmsRatingSettingsIsSuccess,
    },
  ] = ratingApi.useLazyReadRatingSettingsQuery({
    selectFromResult: ({ data, ...rest }) => ({
      ...rest,
      ratingSettings: [...(data?.data.rating_settings ?? [])].sort((a, b) => {
        //sorts the surveys by name in ascending order
        return a?.name ? a.name.localeCompare(b?.name ?? '') : 0;
      }),
    }),
  });

  const dispatch = useAppDispatch();

  const openDeleteModal = async (id: string) => {
    await dispatch(
      appActions.showModal({
        modal: {
          type: 'settings:deleteSurvey',
          arg: { id, readSurveys },
        },
      }),
    );
  };

  const readSurveys = useCallback(() => {
    readRatingSettings({
      level: 'survey',
    });
  }, [readRatingSettings]);

  useEffect(() => {
    readSurveys();
  }, [readSurveys]);

  const navigate = useAppNavigate();

  const handleGoToSurvey = useCallback(
    (settingsId: Satisfaction.CsatSettings['id']) => {
      navigate({
        path: '/settings/satisfaction/phone/survey/:settingsId',
        params: { path: { settingsId } },
      });
    },
    [navigate],
  );

  return (
    <DataTable
      data={surveyRatingSettings ?? []}
      tableLoadingStrategy="spinner"
      tableStatusProps={{
        loading: readSmsRatingSettingsIsFetching,
        loadingFailed: !readSmsRatingSettingsIsSuccess,
        emptyState:
          readSmsRatingSettingsIsSuccess &&
          (surveyRatingSettings ?? []).length < 1
            ? {
                title: 'No configured surveys',
                description: 'Click "Add survey" to set up a new CSAT survey.',
              }
            : undefined,
      }}
      columns={[
        {
          label: 'Name',
          selector: ({ name }) => name,
        },
        {
          label: 'Type',
          style: { width: '10%' },
          selector: ({ survey_type }) =>
            survey_type ? Satisfaction.prettySurveyType(survey_type) : '',
        },
        {
          label: '',
          selector: ({ id }) => (
            <FlexBox flexDirection="row">
              <IconButton
                variant="tertiary"
                icon="pen"
                onClick={() => handleGoToSurvey(id)}
              />
              <IconButton
                variant="tertiary"
                icon="trash"
                onClick={() => openDeleteModal(id)}
              />
            </FlexBox>
          ),
          style: {
            width: 0,
          },
        },
      ]}
    />
  );
}
